import type { EntradaCotizacion, ItemOffgrid, Opciones } from '../api'
import { leerNumero as aNumero } from '../formato'
import { leerComision } from './CampoComision'
import { leerUtilidad } from './CampoUtilidad'

/** Lo que hay escrito en el formulario de on-grid / hibrido, tal cual (textos). */
export interface ValoresEstandar {
  consumo: string
  zona: string
  conBateria: boolean
  categoria: string
  descuento: string
  tarifa: string
  tasa: string
  aMano: boolean
  panel: ItemOffgrid
  inversor: ItemOffgrid
  bateria: ItemOffgrid
  materiales: string
  instalacion: string
  utilidad: string
  /** Comision del vendedor en % tal cual se escribe (vacio = sin comision). */
  comision: string
  /** Costo unitario cambiado en la tabla, por codigo de partida. */
  costos: Record<string, number>
  /** Utilidad propia de cada rubro, en fraccion (0.3 = 30 %). Vacio = todos con la general. */
  margenes: Record<string, number>
}

/**
 * Valida lo escrito y arma la entrada que se le pide al motor. Devuelve un texto si algo no
 * es valido (es lo que se le muestra al asesor). Esta es la primera barrera; la que manda
 * es la del servidor y la del motor.
 */
export function armarEntradaEstandar(v: ValoresEstandar, quitadas: string[], opciones: Opciones | null): EntradaCotizacion | string {
  // Una partida quitada no puede llevar valor manual: se ignora el campo.
  const m = quitadas.includes('materiales_electricos') ? null : aNumero(v.materiales)
  const i = quitadas.includes('instalacion') ? null : aNumero(v.instalacion)
  if (Number.isNaN(m) || (m !== null && m < 0)) return 'Los materiales eléctricos tienen que ser un valor en USD, por ejemplo 350.'
  if (Number.isNaN(i) || (i !== null && i < 0)) return 'La instalación tiene que ser un valor en USD, por ejemplo 400.'
  const c = aNumero(v.consumo)
  const d = aNumero(v.descuento) ?? 0
  const t = aNumero(v.tarifa)
  const s = aNumero(v.tasa)
  if (c === null || !(c > 0)) return 'Escribe el consumo mensual en kWh.'
  if (Number.isNaN(d) || d < 0) return 'El descuento no es un número válido.'
  if (opciones && d / 100 > opciones.descuento_maximo_pct) return `El descuento máximo permitido es ${opciones.descuento_maximo_pct * 100} %.`
  if (Number.isNaN(t) || (t !== null && !(t > 0 && t <= 2))) return 'La tarifa va en USD por kWh, por ejemplo 0,12.'
  if (Number.isNaN(s) || (s !== null && !(s > 0 && s <= 50))) return 'La tasa de descuento va en %, por ejemplo 6,45.'
  const utilidad = leerUtilidad(v.utilidad, opciones?.margen_por_defecto)
  if (typeof utilidad === 'string') return utilidad
  const comision = leerComision(v.comision)
  if (typeof comision === 'string') return comision
  // El campo de materiales/instalacion y el costo cambiado en la tabla son la misma cosa: si
  // el campo tiene valor, manda; si no, vale el de la tabla. Quitada la partida, ninguno.
  const costos = { ...v.costos }
  if (m !== null || quitadas.includes('materiales_electricos')) delete costos.materiales_electricos
  if (i !== null || quitadas.includes('instalacion')) delete costos.instalacion
  const elegidos = [v.panel, v.inversor, ...(v.conBateria ? [v.bateria] : [])]
  if (v.aMano && elegidos.some((f) => !Number.isInteger(f.cantidad) || f.cantidad < 1))
    return 'La cantidad de cada equipo tiene que ser un número entero, al menos 1.'
  return {
    equipos: v.aMano ? { paneles: v.panel, inversor: v.inversor, bateria: v.conBateria ? v.bateria : null } : null,
    consumo_kwh_mes: c,
    zona: v.zona,
    con_bateria: v.conBateria,
    categoria: v.categoria,
    descuento_pct: d / 100,
    tarifa_usd_kwh: t,
    tasa_descuento: s === null ? null : s / 100,
    excluir: quitadas,
    materiales_usd: m,
    instalacion_usd: i,
    margen_pct: utilidad,
    ...(comision === null ? {} : { comision_pct: comision }),
    costos,
    margenes: v.margenes,
  }
}
